// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import Action from 'core/fsmpack/statemachine/actions/Action';

/**
 * @autodoc apply FsmAction
 */
class SpriteAnimationAction extends Action {

  constructor(options) {
    super(...arguments); // also support (id, settings) parameters used with FSM
    this.completed = false;
  }

  static getTransitionLabel() /* transitionKey, actionConfig */{
    return 'Sprite Animation complete';
  }

  enter(fsm) {
    const entity = fsm.getOwnerEntity();
    const meshRendererComponent = entity.meshRendererComponent;
    this.texture = null;
    if (!meshRendererComponent || meshRendererComponent.materials.length === 0) {
      return;
    }
    const material = meshRendererComponent.materials[0];
    this.texture = material.getTexture('DIFFUSE_MAP');
    if (!this.texture) {
      return;
    }
    this.startTime = fsm.getTime();
    this.completed = false;

    this.texture.repeat.setDirect(1 / this.tiling[0], 1 / this.tiling[1]);
  }

  update(fsm) {
    const texture = this.texture;
    if (!texture || this.completed) {
      return;
    }

    const tiling = this.tiling;
    const time = fsm.getTime() - this.startTime;
    const numTiles = tiling[0] * tiling[1];
    let endTile = this.endTile;

    // endTile === -1 means the last tile
    if (endTile === -1) {
      endTile = numTiles;
    } else {
      endTile++;
    }

    let t = time / this.animationTime;
    const loop = Math.floor(t);
    t %= 1;

    if (loop >= this.loops && this.loops !== -1) {
      this.completed = true;
      fsm.send(this.transitions.complete);
      return;
    }

    const numViewTiles = endTile - this.startTile;
    const timeOffset = this.startTile / numTiles;
    t *= numViewTiles / numTiles;
    t += timeOffset;

    const tileX = Math.floor(numTiles * t) % tiling[0];
    const tileY = Math.floor((tiling[1] * t) % tiling[1]);

    texture.offset.setDirect(tileX, tileY).mul(texture.repeat);
    texture.offset.y = -1 / tiling[1] - texture.offset.y + 1;
  }

  exit() /* fsm */{}
}

SpriteAnimationAction.external = {
  key: 'SpriteAnimation',
  deprecatedKey: 'spriteAnimation',
  name: 'Sprite Animation action',
  type: 'texture',
  description: 'Animates a texture sprite sheet over time.',
  canTransition: false,
  parameters: [{
    name: 'Tiling',
    key: 'tiling',
    type: 'vec2',
    description: 'The number of sprites in X and Y directions.',
    default: [8, 8],
  }, {
    name: 'Start tile',
    key: 'startTile',
    type: 'int',
    description: 'Initial tile for the animation. 0 is the first one and numTiles-1 is the last one.',
    default: 0,
  }, {
    name: 'End tile',
    key: 'endTile',
    type: 'int',
    description: 'End tile for the animation. Set to -1 to indicate the last tile.',
    default: -1,
  }, {
    name: 'Animation time',
    key: 'animationTime',
    type: 'float',
    min: 1e-6,
    description: 'The time it should take for the animation to cycle through all the tiles once.',
    default: 1,
  }, {
    name: 'Loops',
    key: 'loops',
    type: 'int',
    description: 'The number times to loop through the tiles before the animation is complete. Set to -1 to animate indefinitely.',
    min: -1,
    default: -1,
  }],
  transitions: [{
    key: 'complete',
    description: 'State to transition to when the animation completes.',
  }],
};

SpriteAnimationAction.register();

export default SpriteAnimationAction;
